/**
 * Список задач проекта — вторая половина экрана проекта, рядом с доской.
 *
 * Отбор общий с доской не по смыслу, а буквально: он живёт в URL (DEV-2040),
 * и оба экрана читают одни и те же ключи адреса. Здесь хранится лишь рабочая
 * копия — то, что уже применено к выдаче.
 *
 * Список живёт в двух режимах. Секционный (группировка включена) — серверные
 * группы с честными счётчиками и догрузкой по одной (DEV-2043): клиентская
 * группировка страницы из 25 строк рвала группы и врала в счётчиках. Плоский
 * (группировка «нет») — прежние страницы по 25.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getProject, listColumns, type Project, type ProjectColumn } from '@/features/projects/api'

import {
  bulkUpdateIssues,
  closeIssue,
  listIssueGroups,
  listIssues,
  moveIssue,
  setAssignee,
  setDueDate,
  setPriority,
  type GroupSlice,
  type IssueRow,
  type IssueSort,
  type IssueSortField,
  type ServerGroup,
  type BulkPatch,
  type ShowTab,
} from './api'

import type { V1Priority } from '@/api/clients/tracker-issue'
import { emptyFilters, hasAnyFilter, type IssueFilters } from './filters'
import {
  defaultViewPrefs,
  loadViewPrefs,
  saveViewPrefs,
  type GroupBy,
  type ListViewPrefs,
} from './listState'

/** Сколько строк на странице плоского режима */
export const PER_PAGE = 25

/** Сколько строк несёт секция при загрузке и сколько добирает «Показать ещё» */
export const PER_GROUP = 10
export const GROUP_STEP = 25

/** Потолок строк одной секции: дальше — плоский режим с фильтром группы */
const GROUP_LIMIT = 100

export const useIssuesStore = defineStore('issues', () => {
  const issues = ref<IssueRow[]>([])
  const groups = ref<ServerGroup[]>([])
  const columns = ref<ProjectColumn[]>([])
  const project = ref<Project | null>(null)
  const projectId = ref('')

  const isLoading = ref(false)
  const error = ref<unknown>(null)

  const filters = ref<IssueFilters>(emptyFilters())
  const tab = ref<ShowTab>('open')
  /** Таб пришёл из адреса — привычка из localStorage его не перебивает */
  const tabFromURL = ref(false)
  const sort = ref<IssueSort>(defaultViewPrefs().sort)
  const grouping = ref<GroupBy>('status')
  const page = ref(1)
  const total = ref(0)

  /** Открытые и закрытые всего отбора — счётчик футера «закрытых N →».
   *  null — счётчик неизвестен: плоский режим его не приносит. */
  const doneTotal = ref<number | null>(null)
  const openTotal = ref<number | null>(null)

  /** Отмеченные строки; Set — Shift-диапазон и сотня отметок без квадратов */
  const selected = ref<Set<string>>(new Set())

  /** Свёрнутые секции — вид, переживает перезагрузку через localStorage */
  const collapsed = ref<Set<string>>(new Set())

  /**
   * Задачи, которые после инлайн-правки числятся не в своей секции (DEV-2043).
   * Политика оптимистики зафиксирована грилем: строка остаётся на месте с
   * пометой «переедет при обновлении», счётчики секций правятся на ±1; сама
   * перестановка — только при перезапросе, чтобы список не прыгал под рукой.
   */
  const pendingRegroup = ref<Set<string>>(new Set())

  const sortHintSeen = ref(false)

  const isSectioned = computed(() => grouping.value !== 'none')

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PER_PAGE)))

  const visibleIssues = computed(() =>
    isSectioned.value ? groups.value.flatMap((group) => group.issues) : issues.value,
  )

  const isEmpty = computed(() => !isLoading.value && visibleIssues.value.length === 0)
  const isFiltered = computed(() => hasAnyFilter(filters.value) || tab.value !== 'open')

  const columnOf = (id: string) => columns.value.find((column) => column.id === id) ?? null

  const persistPrefs = () => {
    if (!project.value) return

    const prefs: ListViewPrefs = {
      sort: sort.value,
      grouping: grouping.value,
      tab: tab.value,
      collapsed: [...collapsed.value],
      sortHintSeen: sortHintSeen.value,
    }

    saveViewPrefs(project.value.key, prefs)
  }

  const load = async () => {
    if (!projectId.value) return

    isLoading.value = true
    error.value = null
    selected.value = new Set()
    pendingRegroup.value = new Set()

    try {
      if (isSectioned.value && grouping.value !== 'none') {
        const result = await listIssueGroups(
          projectId.value,
          filters.value,
          sort.value,
          grouping.value,
          tab.value,
          PER_GROUP,
        )

        groups.value = result.groups
        openTotal.value = result.openTotal
        doneTotal.value = result.doneTotal
        total.value = result.groups.reduce((sum, group) => sum + group.total, 0)
      } else {
        const result = await listIssues(
          projectId.value,
          filters.value,
          sort.value,
          page.value,
          PER_PAGE,
          tab.value,
        )

        issues.value = result.issues
        total.value = result.total
        // Плоский режим счётчик закрытых не приносит; врать футеру нельзя.
        openTotal.value = null
        doneTotal.value = null
      }
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Вход на экран: в адресе ключ проекта (`/p/DEV/issues`), а задачи
   * запрашиваются по идентификатору. Отбор сюда уже положила связка с адресом
   * (useIssueListState) — стор его не трогает; вид достаётся из localStorage.
   */
  const open = async (projectKey: string) => {
    if (project.value?.key === projectKey && visibleIssues.value.length) return

    isLoading.value = true
    error.value = null
    project.value = null
    issues.value = []
    groups.value = []
    columns.value = []
    page.value = 1

    const prefs = loadViewPrefs(projectKey)

    sort.value = prefs.sort
    grouping.value = prefs.grouping
    collapsed.value = new Set(prefs.collapsed)
    sortHintSeen.value = prefs.sortHintSeen

    if (!tabFromURL.value) tab.value = prefs.tab

    try {
      const found = await getProject(projectKey)

      project.value = found
      projectId.value = found.id

      const [, projectColumns] = await Promise.all([load(), listColumns(found.id)])

      columns.value = projectColumns
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /** Отбор и таб из адреса — URL всегда побеждает (DEV-2040) */
  const applyQueryState = async (nextFilters: IssueFilters, nextTab: ShowTab | null) => {
    filters.value = nextFilters

    if (nextTab) {
      tab.value = nextTab
      tabFromURL.value = true
    }

    page.value = 1

    if (projectId.value) await load()
  }

  /** Новый отбор — всегда с первой страницы: на третьей его может не хватить и на одну */
  const applyFilters = async (next: IssueFilters) => {
    filters.value = next
    page.value = 1

    await load()
  }

  /**
   * Таб «Показ» (DEV-2041). Сортировка перещёлкивается вместе с ним, только
   * если стоит дефолтная: «Закрытые» читают по дате закрытия, но выбранный
   * руками порядок таб не переигрывает.
   */
  const setTab = async (next: ShowTab) => {
    if (next === tab.value) return

    const wasDefault =
      (sort.value.field === 'updated_at' || sort.value.field === 'completed_at') && sort.value.desc

    tab.value = next

    if (wasDefault) {
      sort.value = { field: next === 'done' ? 'completed_at' : 'updated_at', desc: true }
    }

    page.value = 1
    persistPrefs()

    await load()
  }

  /**
   * Повторный клик по той же колонке переворачивает порядок — так сортируют
   * везде, и объяснять это не нужно.
   */
  const applySort = async (field: IssueSortField) => {
    sort.value =
      sort.value.field === field
        ? { field, desc: !sort.value.desc }
        : { field, desc: field === 'priority' || field === 'updated_at' || field === 'completed_at' }
    page.value = 1
    persistPrefs()

    await load()
  }

  const setPage = async (next: number) => {
    const clamped = Math.min(Math.max(1, next), pageCount.value)

    if (clamped === page.value) return

    page.value = clamped

    await load()
  }

  const setGrouping = async (next: GroupBy) => {
    if (next === grouping.value) return

    grouping.value = next
    page.value = 1
    persistPrefs()

    await load()
  }

  const toggleCollapsed = (key: string) => {
    if (collapsed.value.has(key)) collapsed.value.delete(key)
    else collapsed.value.add(key)

    collapsed.value = new Set(collapsed.value)
    persistPrefs()
  }

  const markSortHintSeen = () => {
    sortHintSeen.value = true
    persistPrefs()
  }

  /**
   * «Показать ещё» секции: группа перечитывается целиком на больший размер —
   * страницы у сервера выровнены по perPage, и «с 10-й по 35-ю» им не
   * спрашивается, а перечитать три десятка строк дешевле, чем городить
   * смещения.
   */
  const loadMoreGroup = async (key: string) => {
    const group = groups.value.find((entry) => entry.key === key)

    if (!group || grouping.value === 'none') return

    const size = Math.min(group.issues.length + GROUP_STEP, GROUP_LIMIT)
    const slice: GroupSlice = { by: grouping.value, key }

    const result = await listIssues(
      projectId.value,
      filters.value,
      sort.value,
      1,
      size,
      tab.value,
      slice,
    )

    group.issues = result.issues
    group.total = result.total
  }

  const toggleSelected = (id: string) => {
    if (selected.value.has(id)) selected.value.delete(id)
    else selected.value.add(id)

    selected.value = new Set(selected.value)
  }

  /** Диапазон Shift-кликом: от последней отметки до этой, по видимым строкам */
  const selectRange = (fromId: string, toId: string) => {
    const rows = visibleIssues.value
    const from = rows.findIndex((row) => row.id === fromId)
    const to = rows.findIndex((row) => row.id === toId)

    if (from < 0 || to < 0) return

    const [start, end] = from < to ? [from, to] : [to, from]

    const next = new Set(selected.value)

    for (const row of rows.slice(start, end + 1)) next.add(row.id)

    selected.value = next
  }

  const toggleAll = () => {
    selected.value =
      selected.value.size === visibleIssues.value.length
        ? new Set()
        : new Set(visibleIssues.value.map((issue) => issue.id))
  }

  const clearSelection = () => {
    selected.value = new Set()
  }

  /** Какую ось группировки задевает правка строки */
  const axisOf = (change: Partial<IssueRow>): GroupBy | null => {
    if (change.columnId !== undefined) return 'status'
    if (change.assigneeId !== undefined) return 'assignee'
    if (change.priority !== undefined) return 'priority'

    return null
  }

  const findRow = (id: string): IssueRow | undefined =>
    visibleIssues.value.find((entry) => entry.id === id)

  /** Ключ секции, в которой строка числится по значению поля */
  const groupKeyOf = (issue: IssueRow): string => {
    switch (grouping.value) {
      case 'status':
        return issue.columnId
      case 'assignee':
        return issue.assigneeId
      case 'priority':
        return issue.priority
      case 'type':
        return issue.type
      default:
        return ''
    }
  }

  /**
   * Правки из строки применяются сразу и откатываются при отказе: список —
   * рабочий стол, и строка, которая меняется через полсекунды, читается как
   * «не сработало». Перезапроса нет намеренно: он сдвинул бы строки под рукой
   * у того, кто размечает несколько задач подряд.
   *
   * В секционном режиме строка при этом НЕ переезжает (политика DEV-2043):
   * помета и ±1 к счётчикам обеих секций, перестановка — при следующем
   * перезапросе.
   */
  const patch = async (id: string, change: Partial<IssueRow>, send: () => Promise<void>) => {
    const issue = findRow(id)

    if (!issue) return

    const saved = { ...issue }
    const fromKey = groupKeyOf(issue)

    Object.assign(issue, change)

    const axis = axisOf(change)
    const moved = isSectioned.value && axis === grouping.value && groupKeyOf(issue) !== fromKey

    if (moved) {
      pendingRegroup.value.add(id)
      pendingRegroup.value = new Set(pendingRegroup.value)
      shiftGroupTotals(fromKey, groupKeyOf(issue))
    }

    try {
      await send()
    } catch (failure) {
      // Куда строка «переехала» — считаем ДО восстановления: после него оба
      // ключа одинаковы, и счётчики съезжали бы на единицу навсегда.
      const movedTo = groupKeyOf(issue)

      Object.assign(issue, saved)

      if (moved) {
        pendingRegroup.value.delete(id)
        pendingRegroup.value = new Set(pendingRegroup.value)
        shiftGroupTotals(movedTo, fromKey)
      }

      throw failure
    }
  }

  /** ±1 к счётчикам секций при оптимистичном «переезде» строки */
  const shiftGroupTotals = (fromKey: string, toKey: string) => {
    for (const group of groups.value) {
      if (group.key === fromKey) group.total = Math.max(0, group.total - 1)
      if (group.key === toKey) group.total += 1
    }
  }

  /** Смена статуса — это перенос в колонку: другого хранилища статуса нет */
  const moveTo = (id: string, columnId: string) =>
    patch(id, { columnId }, () => moveIssue(id, columnId))

  /** Вместе с именем берём фото: ответ сервера в строку не подставляется,
   *  и без него у нового исполнителя оставались инициалы до перезагрузки. */
  const assignTo = (id: string, assigneeId: string, assigneeName: string, assigneeAvatar = '') =>
    patch(id, { assigneeId, assigneeName, assigneeAvatar }, () => setAssignee(id, assigneeId))

  /** Приоритет из меню и инлайн-пикера (DEV-2052, DEV-2053) */
  const prioritize = (id: string, priority: V1Priority) =>
    patch(id, { priority }, () => setPriority(id, priority))

  /** Срок из меню и инлайн-пикера; пустая строка снимает */
  const setDue = (id: string, dueDate: string) =>
    patch(id, { dueDate }, () => setDueDate(id, dueDate))

  /**
   * «Закрыть» из меню (DEV-2052): done-колонку выбирает сервер, поэтому сперва
   * запрос, потом оптимистика по его ответу. Возвращает прежнюю колонку — на
   * ней стоит «Отменить» в тосте.
   */
  const closeRow = async (id: string): Promise<string> => {
    const issue = findRow(id)

    if (!issue) return ''

    const previous = issue.columnId
    const { columnId } = await closeIssue(id)

    await patch(id, { columnId }, () => Promise.resolve())

    return previous
  }

  /**
   * «Отклонить» из меню (DEV-2271): задача закрывается как несделанная.
   * Колонку ищем по категории, а не по имени: «Отклонено» переименовывают так
   * же свободно, как «Готово» зовут «Выкачено». Возвращает прежнюю колонку —
   * на ней стоит «Отменить» в тосте, как и у закрытия.
   */
  const rejectRow = async (id: string): Promise<string> => {
    const issue = findRow(id)
    const target = columns.value.find(
      (column) => column.statusCategory === 'STATUS_CATEGORY_REJECTED',
    )

    if (!issue || !target) return ''

    const previous = issue.columnId

    await patch(id, { columnId: target.id }, () => moveIssue(id, target.id))

    return previous
  }

  /**
   * Массовые действия — одним вызовом BulkUpdateIssues (DEV-2054): раньше
   * панель слала запрос на задачу и упиралась в лимитер. После пачки — всегда
   * полный перезапрос: локально жонглировать десятками строк по секциям
   * дороже, чем перечитать список (политика DEV-2043).
   */
  const bulkApply = async (bulk: BulkPatch): Promise<{ wipExceeded: boolean }> => {
    const ids = [...selected.value]

    if (!ids.length) return { wipExceeded: false }

    const result = await bulkUpdateIssues(ids, bulk)

    await load()

    return result
  }

  const moveSelected = (columnId: string) => bulkApply({ columnId })

  const assignSelected = (assigneeId: string) => bulkApply({ assigneeId })

  const prioritizeSelected = (priority: V1Priority) => bulkApply({ priority })

  const closeSelected = () => bulkApply({ close: true })

  return {
    issues,
    groups,
    columns,
    project,
    projectId,
    isLoading,
    error,
    filters,
    tab,
    tabFromURL,
    sort,
    grouping,
    page,
    total,
    openTotal,
    doneTotal,
    pageCount,
    selected,
    collapsed,
    pendingRegroup,
    sortHintSeen,
    isSectioned,
    visibleIssues,
    isEmpty,
    isFiltered,
    columnOf,
    open,
    load,
    applyQueryState,
    applyFilters,
    setTab,
    applySort,
    setPage,
    setGrouping,
    toggleCollapsed,
    markSortHintSeen,
    loadMoreGroup,
    toggleSelected,
    selectRange,
    toggleAll,
    clearSelection,
    moveTo,
    assignTo,
    prioritize,
    setDue,
    closeRow,
    rejectRow,
    bulkApply,
    moveSelected,
    assignSelected,
    prioritizeSelected,
    closeSelected,
  }
})
